import { type MouseEvent as ReactMouseEvent, useEffect, useMemo, useState } from "react"
import {
  HeartPulse,
  RefreshCw,
  Search,
  Check,
  X,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  AlertTriangle,
  CheckCircle2,
  Building2,
  CalendarDays,
  Layers,
  Link2,
  Clock,
  ExternalLink,
  type LucideIcon,
} from "lucide-react"
import {
  getStudioCatalogHealth,
  getStudioClassesDetail,
  type StudioCatalogHealth,
  type StudioCatalogStudio,
  type StudioCatalogCronRun,
  type StudioClassesDetail,
  type StudioClassRow,
  type StudioDetailRow,
} from "@/lib/api"
import { toast } from "sonner"
import { cn } from "@/lib/utils"
import { KpiCard } from "@/components/KpiCard"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"

const MODE_LABEL: Record<string, string> = {
  catalog: "Catalog",
  login_required: "Login required",
  live_discovery: "Live discovery",
  appointment: "Appointment",
}

const MODE_COLOR: Record<string, string> = {
  catalog: "bg-emerald-100 text-emerald-800",
  login_required: "bg-amber-100 text-amber-800",
  appointment: "bg-blue-100 text-blue-800",
  live_discovery: "bg-gray-100 text-gray-700",
}

const TIER_LABEL: Record<string, string> = {
  none: "Not a partner",
  non_founding: "Non-founding",
  founding: "Founding",
}

const TIER_COLOR: Record<string, string> = {
  none: "bg-gray-100 text-gray-500",
  non_founding: "bg-emerald-100 text-emerald-800",
  founding: "bg-amber-100 text-amber-800",
}

const CLASS_STATUS_COLOR: Record<string, string> = {
  scheduled: "bg-emerald-100 text-emerald-800",
  full: "bg-amber-100 text-amber-800",
  cancelled: "bg-red-100 text-red-800",
}

type SortKey =
  | "name"
  | "neighborhood"
  | "activity_type"
  | "partner_tier"
  | "booking_system"
  | "platform"
  | "class_source_mode"
  | "configured"
  | "has_playbook"
  | "class_count"
  | "days_covered"
  | "deep_link_pct"
  | "fresh_count"
  | "last_seen_at"

type Tristate = "all" | "yes" | "no"

function timeAgo(iso: string | null): string {
  if (!iso) return "never"
  const then = Date.parse(iso)
  if (Number.isNaN(then)) return "unknown"
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000))
  if (secs < 60) return `${secs}s ago`
  const mins = Math.floor(secs / 60)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

function formatDateTime(iso: string): string {
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return iso
  return new Date(t).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds)) return "-"
  if (seconds < 60) return `${Math.round(seconds)}s`
  const mins = Math.floor(seconds / 60)
  const secs = Math.round(seconds % 60)
  return `${mins}m ${secs}s`
}

function pct(n: number): string {
  return `${Math.round(n)}%`
}

/** ISO day key (YYYY-MM-DD) without TZ drift. */
function formatDay(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return iso
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  })
}

/** Local ISO day key for a Date, without TZ drift. */
function toISODateKey(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

/** Matches scraper scrape_window_days; keeps date tabs and "All N days" in sync. */
const CATALOG_VIEW_DAYS = 14

function nextCatalogDays(): string[] {
  const base = new Date()
  base.setHours(0, 0, 0, 0)
  const out: string[] = []
  for (let i = 0; i < CATALOG_VIEW_DAYS; i++) {
    const d = new Date(base)
    d.setDate(base.getDate() + i)
    out.push(toISODateKey(d))
  }
  return out
}

function formatChipDay(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return iso
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  const weekday = d.toLocaleDateString("en-US", { weekday: "short" })
  return `${weekday} ${Number(m[3])}`
}

function formatClassTime(t: string | null): string {
  if (!t) return "-"
  const m = /^(\d{1,2}):(\d{2})/.exec(t)
  if (!m) return t
  let h = Number(m[1])
  const min = m[2]
  const ampm = h >= 12 ? "PM" : "AM"
  h = h % 12
  if (h === 0) h = 12
  return `${h}:${min} ${ampm}`
}

function withinHours(iso: string | null, hours: number): boolean {
  if (!iso) return false
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return false
  return Date.now() - t <= hours * 3600 * 1000
}

function uniqueSorted(values: (string | null | undefined)[]): string[] {
  const set = new Set<string>()
  for (const v of values) {
    const t = (v ?? "").trim()
    if (t) set.add(t)
  }
  return Array.from(set).sort()
}

export function StudioHealthPage() {
  const [data, setData] = useState<StudioCatalogHealth | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [search, setSearch] = useState("")
  const [activityFilter, setActivityFilter] = useState("all")
  const [tierFilter, setTierFilter] = useState("all")
  const [modeFilter, setModeFilter] = useState("all")
  const [platformFilter, setPlatformFilter] = useState("all")
  const [configuredFilter, setConfiguredFilter] = useState<Tristate>("all")
  const [classesFilter, setClassesFilter] = useState<Tristate>("all")
  const [activeFilter, setActiveFilter] = useState<Tristate>("all")
  const [sortKey, setSortKey] = useState<SortKey>("class_count")
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc")
  const [selectedStudio, setSelectedStudio] = useState<StudioCatalogStudio | null>(null)
  const [drawerWidth, setDrawerWidth] = useState<number>(() =>
    typeof window !== "undefined" ? Math.round(window.innerWidth / 2) : 640
  )

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const health = await getStudioCatalogHealth()
      setData(health)
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to load studio health"
      setError(msg)
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortKey(key)
      // Numeric columns default to descending (most-covered first); name ascending.
      setSortDir(key === "name" ? "asc" : "desc")
    }
  }

  const hasActiveFilters =
    search.trim() !== "" ||
    activityFilter !== "all" ||
    tierFilter !== "all" ||
    modeFilter !== "all" ||
    platformFilter !== "all" ||
    configuredFilter !== "all" ||
    classesFilter !== "all" ||
    activeFilter !== "all"

  function clearFilters() {
    setSearch("")
    setActivityFilter("all")
    setTierFilter("all")
    setModeFilter("all")
    setPlatformFilter("all")
    setConfiguredFilter("all")
    setClassesFilter("all")
    setActiveFilter("all")
  }

  const activityOptions = useMemo(
    () => uniqueSorted((data?.studios ?? []).map((s) => s.activity_type)),
    [data],
  )
  const tierOptions = useMemo(
    () => uniqueSorted((data?.studios ?? []).map((s) => s.partner_tier)),
    [data],
  )
  const platformOptions = useMemo(
    () => uniqueSorted((data?.studios ?? []).map((s) => s.platform)),
    [data],
  )
  const modeOptions = useMemo(() => {
    const set = new Set<string>()
    for (const row of data?.by_source_mode ?? []) set.add(row.mode)
    for (const s of data?.studios ?? []) if (s.class_source_mode) set.add(s.class_source_mode)
    return Array.from(set).sort()
  }, [data])

  const filtered = useMemo(() => {
    const studios = data?.studios ?? []
    const q = search.trim().toLowerCase()
    return studios.filter((s) => {
      if (activeFilter === "yes" && !s.is_active) return false
      if (activeFilter === "no" && s.is_active) return false
      if (activityFilter !== "all" && s.activity_type !== activityFilter) return false
      if (tierFilter !== "all" && s.partner_tier !== tierFilter) return false
      if (modeFilter !== "all" && s.class_source_mode !== modeFilter) return false
      if (platformFilter !== "all" && (s.platform ?? "") !== platformFilter) return false
      if (configuredFilter === "yes" && !s.configured) return false
      if (configuredFilter === "no" && s.configured) return false
      if (classesFilter === "yes" && s.class_count <= 0) return false
      if (classesFilter === "no" && s.class_count > 0) return false
      if (!q) return true
      return (
        s.name.toLowerCase().includes(q) ||
        (s.neighborhood ?? "").toLowerCase().includes(q) ||
        (s.location_pill ?? "").toLowerCase().includes(q) ||
        (s.booking_system ?? "").toLowerCase().includes(q) ||
        (s.platform ?? "").toLowerCase().includes(q)
      )
    })
  }, [
    data,
    search,
    activityFilter,
    tierFilter,
    modeFilter,
    platformFilter,
    configuredFilter,
    classesFilter,
    activeFilter,
  ])

  const sorted = useMemo(() => {
    const valueOf = (s: StudioCatalogStudio): string | number => {
      switch (sortKey) {
        case "class_count":
          return s.class_count
        case "days_covered":
          return s.days_covered
        case "deep_link_pct":
          return s.deep_link_pct ?? -1
        case "fresh_count":
          return s.fresh_count
        case "neighborhood":
          return (s.neighborhood || s.location_pill || "").toLowerCase()
        case "activity_type":
          return (s.activity_type || "").toLowerCase()
        case "partner_tier":
          // partners first, then by tier label
          return `${s.is_partner ? "0" : "1"}${s.partner_tier || ""}`.toLowerCase()
        case "booking_system":
          return (s.booking_system || "").toLowerCase()
        case "platform":
          return (s.platform || "").toLowerCase()
        case "class_source_mode":
          return (s.class_source_mode || "").toLowerCase()
        case "configured":
          return Number(s.configured)
        case "has_playbook":
          return Number(s.has_playbook)
        case "last_seen_at":
          return s.last_seen_at || ""
        default:
          return s.name.toLowerCase()
      }
    }
    const dir = sortDir === "asc" ? 1 : -1
    return [...filtered].sort((a, b) => {
      const av = valueOf(a)
      const bv = valueOf(b)
      let cmp: number
      if (typeof av === "number" && typeof bv === "number") cmp = av - bv
      else cmp = String(av).localeCompare(String(bv))
      // Keep active studios above inactive within a tie, then name.
      if (cmp === 0) cmp = Number(b.is_active) - Number(a.is_active)
      if (cmp === 0) cmp = a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      return cmp * dir
    })
  }, [filtered, sortKey, sortDir])

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <HeartPulse className="size-6 text-muted-foreground" /> Studio Health
        </h1>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-lg border bg-muted" />
          ))}
        </div>
        <div className="h-64 animate-pulse rounded-lg border bg-muted" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="space-y-4 p-6">
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <HeartPulse className="size-6 text-muted-foreground" /> Studio Health
        </h1>
        <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-6 text-center">
          <AlertTriangle className="mx-auto mb-2 size-8 text-destructive" />
          <p className="text-sm text-destructive">{error ?? "No data"}</p>
          <button
            onClick={() => void load()}
            className="mt-3 inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm hover:bg-accent"
          >
            <RefreshCw className="size-3.5" /> Retry
          </button>
        </div>
      </div>
    )
  }

  const o = data.overall

  return (
    <div
      className="space-y-6 p-6 transition-[padding] duration-150"
      style={selectedStudio ? { paddingRight: drawerWidth + 24 } : undefined}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <HeartPulse className="size-6" /> Studio Health
          </h1>
          <p className="max-w-2xl text-sm text-muted-foreground">
            Class-catalog scraper coverage across the studio directory: which
            studios are configured, how many carry fresh classes, and deep-link /
            playbook coverage.{" "}
            <span title={formatDateTime(data.generated_at)}>
              Updated {timeAgo(data.generated_at)}.
            </span>
          </p>
        </div>
        <button
          onClick={() => void load()}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm hover:bg-accent"
        >
          <RefreshCw className={cn("size-3.5", loading && "animate-spin")} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        <KpiCard
          title="Active studios"
          value={o.studios_active}
          icon={Building2}
          subtitle={`${o.studios_inactive} inactive · ${o.studios_total} total`}
        />
        <KpiCard
          title="Configured"
          value={`${o.configured}/${o.studios_active}`}
          icon={CheckCircle2}
          subtitle={`${o.unconfigured} unconfigured`}
          variant={o.unconfigured > 0 ? "warning" : "success"}
        />
        <KpiCard
          title="Studios with classes"
          value={o.studios_with_classes}
          icon={CalendarDays}
          subtitle={`of ${o.studios_active} active`}
        />
        <KpiCard title="Total classes" value={o.classes_total} icon={Layers} />
        <KpiCard
          title="Deep-link %"
          value={pct(o.deep_link_pct)}
          icon={Link2}
          subtitle="classes with a booking deep link"
          variant={o.deep_link_pct >= 100 ? "success" : "default"}
        />
        <KpiCard
          title="Fresh %"
          value={pct(o.fresh_pct)}
          icon={Clock}
          subtitle={`${o.studios_with_playbook}/${o.playbooks_total} playbooks mapped`}
          variant={o.fresh_pct < 100 ? "warning" : "success"}
        />
      </div>

      <AttentionSection attention={data.attention} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <BreakdownCard title="By platform">
          <MiniTable
            head={["Platform", "Studios", "Classes"]}
            rows={data.by_platform.map((p) => [
              p.platform || "-",
              p.studios,
              p.classes,
            ])}
          />
        </BreakdownCard>

        <BreakdownCard title="By partner tier">
          <MiniTable
            head={["Tier", "Active", "Config", "w/ classes"]}
            rows={data.by_tier.map((t) => [
              <TierBadge key={t.partner_tier} tier={t.partner_tier} />,
              t.active,
              t.configured,
              t.with_classes,
            ])}
          />
        </BreakdownCard>

        <BreakdownCard title="By class source mode">
          <MiniTable
            head={["Mode", "Active studios"]}
            rows={data.by_source_mode.map((m) => [
              <ModeBadge key={m.mode} mode={m.mode} />,
              m.active_studios,
            ])}
          />
        </BreakdownCard>
      </div>

      <CronRunsCard runs={data.cron_runs} />

      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[240px] flex-1">
            <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search name, neighborhood, booking system, platform..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>
          <span className="whitespace-nowrap text-sm text-muted-foreground">
            {sorted.length} of {data.studios.length} studios
          </span>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs hover:bg-accent"
            >
              <X className="size-3" /> Clear filters
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <FilterSelect
            title="Activity type"
            value={activityFilter}
            onChange={setActivityFilter}
            allLabel="All activities"
            options={activityOptions}
          />
          <FilterSelect
            title="Partner tier"
            value={tierFilter}
            onChange={setTierFilter}
            allLabel="All tiers"
            options={tierOptions}
            labelMap={TIER_LABEL}
          />
          <FilterSelect
            title="Class source mode"
            value={modeFilter}
            onChange={setModeFilter}
            allLabel="All modes"
            options={modeOptions}
            labelMap={MODE_LABEL}
          />
          <FilterSelect
            title="Platform"
            value={platformFilter}
            onChange={setPlatformFilter}
            allLabel="All platforms"
            options={platformOptions}
          />
          <TristateSelect
            title="Configured"
            value={configuredFilter}
            onChange={setConfiguredFilter}
            allLabel="Any config"
            yesLabel="Configured"
            noLabel="Unconfigured"
          />
          <TristateSelect
            title="Has classes"
            value={classesFilter}
            onChange={setClassesFilter}
            allLabel="Any classes"
            yesLabel="With classes"
            noLabel="No classes"
          />
          <TristateSelect
            title="Active"
            value={activeFilter}
            onChange={setActiveFilter}
            allLabel="Active + inactive"
            yesLabel="Active only"
            noLabel="Inactive only"
          />
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="w-8 px-2 py-2" />
              <SortHeader label="Name" col="name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortHeader label="Neighborhood" col="neighborhood" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortHeader label="Activity" col="activity_type" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortHeader label="Partner" col="partner_tier" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortHeader label="Booking system" col="booking_system" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortHeader label="Platform" col="platform" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortHeader label="Mode" col="class_source_mode" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortHeader label="Config" col="configured" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="center" />
              <SortHeader label="Playbook" col="has_playbook" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="center" />
              <SortHeader label="Classes" col="class_count" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="right" />
              <SortHeader label="Days" col="days_covered" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="right" />
              <SortHeader label="Deep-link" col="deep_link_pct" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="right" />
              <SortHeader label="Fresh" col="fresh_count" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="center" />
              <SortHeader label="Last seen" col="last_seen_at" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            </tr>
          </thead>
          <tbody>
            {sorted.map((s) => (
              <StudioRow key={s.id} studio={s} onSelect={() => setSelectedStudio(s)} />
            ))}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={15} className="px-3 py-8 text-center text-muted-foreground">
                  No studios match your filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <StudioDrawer
        studio={selectedStudio}
        onClose={() => setSelectedStudio(null)}
        width={drawerWidth}
        onWidth={setDrawerWidth}
      />
    </div>
  )
}

function StudioRow({
  studio: s,
  onSelect,
}: {
  studio: StudioCatalogStudio
  onSelect: () => void
}) {
  return (
    <tr
      className={cn(
        "cursor-pointer border-t hover:bg-muted/30",
        !s.is_active && "opacity-50",
      )}
      onClick={onSelect}
    >
      <td className="px-2 py-2 text-muted-foreground">
        <ChevronRight className="size-4" />
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-2 font-medium">
          {s.name}
          {!s.is_active && (
            <Badge className="bg-gray-100 font-normal text-gray-500">inactive</Badge>
          )}
        </div>
        {s.city && <div className="text-xs text-muted-foreground">{s.city}</div>}
      </td>
      <td className="px-3 py-2 text-muted-foreground">
        {s.neighborhood || s.location_pill || "-"}
      </td>
      <td className="px-3 py-2 capitalize">{s.activity_type || "-"}</td>
      <td className="px-3 py-2">
        {s.is_partner ? <TierBadge tier={s.partner_tier} /> : <Dash />}
      </td>
      <td className="px-3 py-2">{s.booking_system || <Dash />}</td>
      <td className="px-3 py-2">{s.platform || <Dash />}</td>
      <td className="px-3 py-2">
        <ModeBadge mode={s.class_source_mode} />
      </td>
      <td className="px-3 py-2 text-center">
        <Bool value={s.configured} />
      </td>
      <td className="px-3 py-2 text-center" title={s.playbook_file ?? undefined}>
        <Bool value={s.has_playbook} />
      </td>
      <td className="px-3 py-2 text-right tabular-nums">{s.class_count}</td>
      <td className="px-3 py-2 text-right tabular-nums">{s.days_covered}</td>
      <td className="px-3 py-2 text-right tabular-nums" title={`${s.deep_link_count} of ${s.class_count} classes`}>
        {s.class_count > 0 ? pct(s.deep_link_pct) : "-"}
      </td>
      <td className="px-3 py-2 text-center">
        {s.fresh_count > 0 ? (
          <span className="inline-flex items-center gap-1 text-emerald-600" title={`${s.fresh_count} fresh classes`}>
            <Check className="size-4" />
          </span>
        ) : (
          <Badge className="bg-amber-100 font-normal text-amber-800">stale</Badge>
        )}
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-muted-foreground" title={s.last_seen_at ?? undefined}>
        {timeAgo(s.last_seen_at)}
      </td>
    </tr>
  )
}

function StudioDrawer({
  studio,
  onClose,
  width,
  onWidth,
}: {
  studio: StudioCatalogStudio | null
  onClose: () => void
  width: number
  onWidth: (w: number) => void
}) {
  const [detail, setDetail] = useState<StudioClassesDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function startResize(e: ReactMouseEvent) {
    e.preventDefault()
    const onMove = (ev: MouseEvent) => {
      const next = window.innerWidth - ev.clientX
      const max = window.innerWidth * 0.9
      onWidth(Math.max(380, Math.min(next, max)))
    }
    const onUp = () => {
      document.removeEventListener("mousemove", onMove)
      document.removeEventListener("mouseup", onUp)
      document.body.style.userSelect = ""
    }
    document.body.style.userSelect = "none"
    document.addEventListener("mousemove", onMove)
    document.addEventListener("mouseup", onUp)
  }

  useEffect(() => {
    if (!studio) return
    let cancelled = false
    setDetail(null)
    setError(null)
    setLoading(true)
    getStudioClassesDetail(studio.id)
      .then((d) => {
        if (!cancelled) setDetail(d)
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load studio detail")
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [studio])

  const subtitleParts = [
    studio?.neighborhood || studio?.location_pill,
    studio?.city,
  ].filter(Boolean)

  if (!studio) return null

  return (
    // No backdrop — list stays scrollable; clicking another row swaps content.
    <aside
      className="fixed right-0 top-0 z-40 flex h-screen flex-col border-l bg-background shadow-2xl"
      style={{ width }}
    >
      <div
        onMouseDown={startResize}
        title="Drag to resize"
        className="absolute left-0 top-0 h-full w-1.5 cursor-col-resize bg-transparent hover:bg-primary/20"
      />
      <div className="flex shrink-0 items-start justify-between gap-2 border-b p-4">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 truncate text-lg font-semibold">
            {studio.name}
            {!studio.is_active && (
              <Badge className="bg-gray-100 font-normal text-gray-500">inactive</Badge>
            )}
          </h2>
          <p className="text-xs text-muted-foreground">
            {subtitleParts.length ? subtitleParts.join(" · ") : "Studio detail"}
          </p>
        </div>
        <button
          onClick={onClose}
          className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* min-h-0 lets overflow-y-auto engage; without it the Classes section is unreachable. */}
      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto overscroll-contain p-4">
        {loading && (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-20 animate-pulse rounded-md border bg-muted" />
            ))}
          </div>
        )}

        {error && (
          <div className="rounded-md border border-destructive/50 bg-destructive/5 p-4 text-sm text-destructive">
            {error}
          </div>
        )}

        {detail && (
          <>
            {detail.studio ? (
              <StudioDetailsSection
                studio={detail.studio}
                playbookFile={detail.playbook_file}
              />
            ) : (
              <p className="text-sm text-muted-foreground">Studio record not found.</p>
            )}
            <CoverageStrip detail={detail} />
            <ClassesSection detail={detail} />
          </>
        )}
      </div>
    </aside>
  )
}

function StudioDetailsSection({
  studio,
  playbookFile,
}: {
  studio: StudioDetailRow
  playbookFile: string | null
}) {
  return (
    <section>
      <SectionTitle icon={Building2}>Studio details</SectionTitle>
      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
        <KV label="Name">{orDash(studio.name)}</KV>
        <KV label="ID">
          <span className="font-mono text-xs break-all">{studio.id}</span>
        </KV>
        <KV label="Active">
          {studio.is_active ? (
            <Badge className="bg-emerald-100 font-normal text-emerald-800">active</Badge>
          ) : (
            <Badge className="bg-gray-100 font-normal text-gray-500">inactive</Badge>
          )}
        </KV>
        <KV label="Activity type">
          <span className="capitalize">{orDash(studio.activity_type)}</span>
        </KV>
        <KV label="Partner">
          {studio.is_partner ? <TierBadge tier={studio.partner_tier} /> : <Dash />}
        </KV>
        <KV label="Class source mode">
          <ModeBadge mode={studio.class_source_mode} />
        </KV>
        <KV label="Booking system">{orDash(studio.booking_system)}</KV>
        <KV label="Booking method">{orDash(studio.booking_method)}</KV>
        <KV label="Priority">{orDash(studio.priority)}</KV>
        <KV label="Display order">{orDash(studio.display_order)}</KV>
        <KV label="City">{orDash(studio.city)}</KV>
        <KV label="Neighborhood">
          {orDash(studio.neighborhood || studio.location_pill)}
        </KV>
        <KV label="Timezone">{orDash(studio.timezone)}</KV>
        <KV label="External ID">
          <span className="font-mono text-xs break-all">{studio.external_id || "-"}</span>
        </KV>
        <KV label="Created">
          {studio.created_at ? formatDateTime(studio.created_at) : <Dash />}
        </KV>
        <KV label="Updated">
          {studio.updated_at ? formatDateTime(studio.updated_at) : <Dash />}
        </KV>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3">
        <KV label="Address">{orDash(studio.address)}</KV>
        <KV label="Website">
          {studio.url ? (
            <a
              href={studio.url}
              target="_blank"
              rel="noopener"
              className="inline-flex items-center gap-1 break-all text-primary hover:underline"
            >
              {studio.url}
              <ExternalLink className="size-3.5 shrink-0" />
            </a>
          ) : (
            <Dash />
          )}
        </KV>
        <KV label="Playbook file">
          {playbookFile ? (
            <span className="font-mono text-xs break-all">{playbookFile}</span>
          ) : (
            <span className="text-muted-foreground">none</span>
          )}
        </KV>
      </div>

      <div className="mt-3">
        <div className="mb-1 text-[11px] uppercase tracking-wide text-muted-foreground">
          scrape_config
        </div>
        <pre className="max-h-64 overflow-x-auto rounded-md border bg-muted p-2 font-mono text-[11px] leading-relaxed">
          {JSON.stringify(studio.scrape_config ?? {}, null, 2)}
        </pre>
      </div>
    </section>
  )
}

function CoverageStrip({ detail }: { detail: StudioClassesDetail }) {
  const classes = detail.classes
  const withLinks = classes.filter((c) => c.booking_url).length
  const cancelled = classes.filter((c) => c.status === "cancelled").length
  const fresh = classes.filter((c) => withinHours(c.last_seen_at, 24)).length
  const open = classes.filter((c) => c.availability === "open").length
  const booked = classes.filter((c) => c.availability === "full").length
  const hasAvail = open + booked > 0
  const tiles: { label: string; value: number }[] = [
    { label: "Classes", value: detail.class_count },
    { label: "Days covered", value: detail.days.length },
    { label: "Deep links", value: withLinks },
    ...(hasAvail
      ? [
          { label: "Open now", value: open },
          { label: "Booked out", value: booked },
        ]
      : []),
    { label: "Cancelled", value: cancelled },
    { label: "Fresh (24h)", value: fresh },
  ]
  return (
    <section>
      <SectionTitle icon={Layers}>Coverage</SectionTitle>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
        {tiles.map((t) => (
          <div key={t.label} className="rounded-md border bg-card p-2 text-center">
            <div className="text-lg font-semibold tabular-nums">{t.value}</div>
            <div className="text-[11px] text-muted-foreground">{t.label}</div>
          </div>
        ))}
      </div>
    </section>
  )
}

function ClassesSection({ detail }: { detail: StudioClassesDetail }) {
  // Client-side date window (not from API) so the founder sees what the agent would see.
  const week = useMemo(() => nextCatalogDays(), [])
  const [selectedDay, setSelectedDay] = useState<string>("today")

  if (detail.class_count === 0) {
    return (
      <section>
        <SectionTitle icon={CalendarDays}>Classes</SectionTitle>
        <EmptyClassesNote mode={detail.studio?.class_source_mode ?? ""} />
      </section>
    )
  }

  const studioUrl = detail.studio?.url ?? null
  const showAll = selectedDay === "all"
  const activeDay = selectedDay === "today" ? week[0] : selectedDay

  return (
    <section>
      <SectionTitle icon={CalendarDays}>Classes ({detail.class_count})</SectionTitle>

      <div className="mb-3 flex flex-wrap gap-1.5">
        {week.map((day, i) => {
          const count = (detail.by_day[day] ?? []).length
          const active = !showAll && day === activeDay
          return (
            <button
              key={day}
              onClick={() => setSelectedDay(day)}
              title={formatDay(day)}
              className={cn(
                "flex flex-col items-center rounded-md border px-2.5 py-1.5 text-xs transition-colors hover:bg-accent",
                active && "border-primary bg-primary/10 text-foreground",
              )}
            >
              <span className="font-medium">{i === 0 ? "Today" : formatChipDay(day)}</span>
              <span className={cn("tabular-nums text-muted-foreground", count === 0 && "opacity-60")}>
                {count} {count === 1 ? "class" : "classes"}
              </span>
            </button>
          )
        })}
        <button
          onClick={() => setSelectedDay("all")}
          className={cn(
            "flex flex-col items-center justify-center rounded-md border px-2.5 py-1.5 text-xs transition-colors hover:bg-accent",
            showAll && "border-primary bg-primary/10 text-foreground",
          )}
        >
          <span className="font-medium">All {week.length} days</span>
          <span className="tabular-nums text-muted-foreground">{detail.class_count} total</span>
        </button>
      </div>

      {showAll ? (
        <div className="space-y-4">
          {detail.days.map((day) => (
            <DayGroup key={day} day={day} rows={detail.by_day[day] ?? []} studioUrl={studioUrl} />
          ))}
        </div>
      ) : (
        <DayGroup day={activeDay} rows={detail.by_day[activeDay] ?? []} studioUrl={studioUrl} />
      )}
    </section>
  )
}

function DayGroup({
  day,
  rows,
  studioUrl,
}: {
  day: string
  rows: StudioClassRow[]
  studioUrl: string | null
}) {
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2 text-sm font-medium">
        {formatDay(day)}
        <span className="text-xs font-normal text-muted-foreground">
          {rows.length} {rows.length === 1 ? "class" : "classes"}
        </span>
      </div>
      {rows.length === 0 ? (
        <div className="rounded-md border border-dashed bg-muted/30 p-3 text-xs text-muted-foreground">
          No classes
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((c) => (
            <ClassCard key={c.id} c={c} studioUrl={studioUrl} />
          ))}
        </div>
      )}
    </div>
  )
}

function EmptyClassesNote({ mode }: { mode: string }) {
  let msg: string
  if (mode === "appointment") {
    msg = "Appointment-based (recovery) - see scrape_config.services."
  } else if (mode === "login_required") {
    msg = "Classes are behind studio login."
  } else if (mode === "live_discovery") {
    msg = "Live-discovery studio - classes are found at booking time, not pre-scraped."
  } else {
    msg = "No classes in the catalog yet."
  }
  return (
    <div className="rounded-md border border-dashed bg-muted/30 p-4 text-sm text-muted-foreground">
      {msg}
    </div>
  )
}

function ClassCard({ c, studioUrl }: { c: StudioClassRow; studioUrl?: string | null }) {
  const muscles = c.muscle_groups ?? []
  const exercises = c.exercise_list ?? []
  // Some platforms (e.g. walla) expose separate in-person and livestream booking links.
  const meta = c.metadata ?? {}
  const inPersonUrl = typeof meta.in_person_url === "string" ? meta.in_person_url : null
  const livestreamUrl = typeof meta.livestream_url === "string" ? meta.livestream_url : null
  return (
    <div className="rounded-md border bg-card p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{formatClassTime(c.start_time)}</span>
            {c.duration_min != null && (
              <span className="text-xs text-muted-foreground">{c.duration_min} min</span>
            )}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-2">
            <span className="font-medium">{c.name || "-"}</span>
            {c.category && (
              <Badge className="bg-gray-100 font-normal text-gray-700">{c.category}</Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <AvailabilityBadge availability={c.availability} waitlist={c.waitlist_available} />
          <ClassStatusBadge status={c.status} />
          <span className="text-xs text-muted-foreground">
            {c.spots_available != null ? `${c.spots_available} spots` : "-"}
          </span>
        </div>
      </div>

      <div className="mt-2 grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
        <ClassField label="Instructor">{c.instructor || <Dash />}</ClassField>
        <ClassField label="Other details">{c.other_details || <Dash />}</ClassField>
        <ClassField label="Muscle groups">
          {muscles.length ? <Chips items={muscles} /> : <Dash />}
        </ClassField>
        <ClassField label="Exercises">
          {exercises.length ? <Chips items={exercises} /> : <Dash />}
        </ClassField>
      </div>

      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        {inPersonUrl && livestreamUrl ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <a
              href={inPersonUrl}
              target="_blank"
              rel="noopener"
              className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium hover:bg-accent"
            >
              <ExternalLink className="size-3.5" /> In-person
            </a>
            <a
              href={livestreamUrl}
              target="_blank"
              rel="noopener"
              className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium hover:bg-accent"
            >
              <ExternalLink className="size-3.5" /> Livestream
            </a>
          </div>
        ) : c.booking_url ? (
          <a
            href={c.booking_url}
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium hover:bg-accent"
          >
            <ExternalLink className="size-3.5" /> Open booking link
          </a>
        ) : studioUrl ? (
          <a
            href={studioUrl}
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium hover:bg-accent"
          >
            <ExternalLink className="size-3.5" /> Open studio site
          </a>
        ) : (
          <span className="text-xs text-muted-foreground">
            no per-class link - falls back to studio site
          </span>
        )}
        <span
          className="max-w-[55%] truncate text-[11px] text-muted-foreground"
          title={c.external_key ?? undefined}
        >
          {c.source || "-"}
          {c.external_key ? ` · ${c.external_key}` : ""}
        </span>
      </div>

      {/* Selectable URL for copy/paste — not just the action button. */}
      {c.booking_url ? (
        <div
          className="mt-1.5 break-all font-mono text-xs text-muted-foreground"
          title={c.booking_url}
        >
          {c.booking_url}
        </div>
      ) : studioUrl ? (
        <div className="mt-1.5 text-xs text-muted-foreground">
          no per-class link - falls back to studio site:{" "}
          <span className="break-all font-mono" title={studioUrl}>
            {studioUrl}
          </span>
        </div>
      ) : null}
    </div>
  )
}

function AttentionSection({ attention }: { attention: StudioCatalogHealth["attention"] }) {
  const cards: {
    key: string
    label: string
    names: string[]
    variant: "default" | "warning" | "danger"
    note?: string
  }[] = [
    {
      key: "unconfigured_partners",
      label: "Unconfigured partners",
      names: attention.unconfigured_partners,
      variant: "danger",
      note: "No scrape config yet. The scraper tries to auto-detect each studio's platform from its URL on the next run; any it cannot detect stay here.",
    },
    {
      key: "configured_no_classes",
      label: "Configured but no classes",
      names: attention.configured_no_classes,
      variant: "warning",
    },
    {
      key: "stale_over_24h",
      label: "Stale (no data in 24h+)",
      names: attention.stale_over_24h,
      variant: "warning",
    },
    {
      key: "missing_deep_links",
      label: "Missing deep links",
      names: attention.missing_deep_links,
      variant: "default",
    },
  ]
  const active = cards.filter((c) => c.names.length > 0)

  if (active.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50/50 p-4 text-sm">
        <CheckCircle2 className="size-5 text-green-600" />
        <span className="font-medium">All clear</span>
        <span className="text-muted-foreground">
          Every active studio is configured, carrying fresh classes with deep
          links.
        </span>
      </div>
    )
  }

  const tint: Record<string, string> = {
    danger: "border-red-200 bg-red-50/50",
    warning: "border-amber-200 bg-amber-50/50",
    default: "bg-card",
  }

  return (
    <div>
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
        <AlertTriangle className="size-4 text-amber-600" /> Needs attention
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {active.map((c) => (
          <div key={c.key} className={cn("rounded-lg border p-4", tint[c.variant])}>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium">{c.label}</span>
              <span className="text-xs tabular-nums text-muted-foreground">
                {c.names.length}
              </span>
            </div>
            {c.note && (
              <p className="mb-2 text-xs text-muted-foreground">{c.note}</p>
            )}
            <div className="flex flex-wrap gap-1.5">
              {c.names.map((n) => (
                <span
                  key={n}
                  className="rounded-md border bg-background px-2 py-0.5 text-xs"
                >
                  {n}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function CronRunsCard({ runs }: { runs: StudioCatalogCronRun[] }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
        <RefreshCw className="size-4 text-muted-foreground" /> Recent scraper runs
      </div>
      {runs.length === 0 ? (
        <p className="py-4 text-center text-sm text-muted-foreground">
          No scraper runs recorded yet.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">Executed</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 text-right font-medium">Processed</th>
                <th className="px-3 py-2 text-right font-medium">OK</th>
                <th className="px-3 py-2 text-right font-medium">Failed</th>
                <th className="px-3 py-2 text-right font-medium">Duration</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r, i) => {
                const ok = r.status === "success" || r.status === "ok"
                return (
                  <tr key={`${r.executed_at}-${i}`} className="border-t">
                    <td className="px-3 py-2 whitespace-nowrap" title={r.errors?.join("\n") || undefined}>
                      {formatDateTime(r.executed_at)}
                    </td>
                    <td className="px-3 py-2">
                      <Badge
                        className={cn(
                          "font-normal",
                          ok
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-red-100 text-red-800",
                        )}
                      >
                        {r.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.users_processed}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-emerald-600">
                      {r.users_succeeded}
                    </td>
                    <td
                      className={cn(
                        "px-3 py-2 text-right tabular-nums",
                        r.users_failed > 0 && "text-red-600",
                      )}
                    >
                      {r.users_failed}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatDuration(r.duration_seconds)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function BreakdownCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-2 text-sm font-semibold">{title}</div>
      {children}
    </div>
  )
}

function MiniTable({
  head,
  rows,
}: {
  head: React.ReactNode[]
  rows: React.ReactNode[][]
}) {
  if (rows.length === 0) {
    return <p className="py-3 text-center text-xs text-muted-foreground">No data</p>
  }
  return (
    <table className="w-full text-sm">
      <thead className="text-left text-xs uppercase text-muted-foreground">
        <tr>
          {head.map((h, i) => (
            <th key={i} className={cn("py-1.5 font-medium", i > 0 && "text-right")}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((cells, ri) => (
          <tr key={ri} className="border-t">
            {cells.map((c, ci) => (
              <td key={ci} className={cn("py-1.5 tabular-nums", ci > 0 && "text-right")}>
                {c}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function SortHeader({
  label,
  col,
  sortKey,
  sortDir,
  onSort,
  align = "left",
}: {
  label: string
  col: SortKey
  sortKey: SortKey
  sortDir: "asc" | "desc"
  onSort: (k: SortKey) => void
  align?: "left" | "right" | "center"
}) {
  const active = sortKey === col
  return (
    <th className="px-3 py-2 font-medium">
      <button
        onClick={() => onSort(col)}
        className={cn(
          "-mx-1 flex items-center gap-1 rounded px-1 py-0.5 hover:text-foreground",
          align === "right" && "ml-auto",
          align === "center" && "mx-auto",
          active && "text-foreground",
        )}
        title={`Sort by ${label}`}
      >
        {label}
        {active ? (
          sortDir === "asc" ? (
            <ChevronUp className="size-3.5" />
          ) : (
            <ChevronDown className="size-3.5" />
          )
        ) : (
          <ChevronsUpDown className="size-3.5 opacity-40" />
        )}
      </button>
    </th>
  )
}

function FilterSelect({
  value,
  onChange,
  allLabel,
  options,
  labelMap,
  title,
}: {
  value: string
  onChange: (v: string) => void
  allLabel: string
  options: string[]
  labelMap?: Record<string, string>
  title?: string
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      title={title}
      className="h-9 rounded-md border border-input bg-background px-3 text-sm"
    >
      <option value="all">{allLabel}</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {labelMap?.[o] ?? o}
        </option>
      ))}
    </select>
  )
}

function TristateSelect({
  value,
  onChange,
  allLabel,
  yesLabel,
  noLabel,
  title,
}: {
  value: Tristate
  onChange: (v: Tristate) => void
  allLabel: string
  yesLabel: string
  noLabel: string
  title?: string
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as Tristate)}
      title={title}
      className="h-9 rounded-md border border-input bg-background px-3 text-sm"
    >
      <option value="all">{allLabel}</option>
      <option value="yes">{yesLabel}</option>
      <option value="no">{noLabel}</option>
    </select>
  )
}

function SectionTitle({
  icon: Icon,
  children,
}: {
  icon: LucideIcon
  children: React.ReactNode
}) {
  return (
    <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
      <Icon className="size-4 text-muted-foreground" /> {children}
    </div>
  )
}

function KV({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="min-w-0 wrap-break-word text-sm">{children}</span>
    </div>
  )
}

function ClassField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="min-w-0 wrap-break-word">{children}</span>
    </div>
  )
}

function Chips({ items }: { items: string[] }) {
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((it, i) => (
        <span
          key={`${it}-${i}`}
          className="rounded border bg-background px-1.5 py-0.5 text-[11px]"
        >
          {it}
        </span>
      ))}
    </div>
  )
}

function ClassStatusBadge({ status }: { status: string }) {
  return (
    <Badge className={cn("font-normal", CLASS_STATUS_COLOR[status] ?? "bg-gray-100 text-gray-700")}>
      {status || "-"}
    </Badge>
  )
}

const AVAILABILITY_COLOR: Record<string, string> = {
  open: "bg-emerald-100 text-emerald-700",
  waitlist: "bg-amber-100 text-amber-700",
  full: "bg-rose-100 text-rose-700",
  ended: "bg-gray-100 text-gray-500",
  unknown: "bg-gray-100 text-gray-500",
}

/** Hidden when unknown — many platforms don't expose spot counts. */
function AvailabilityBadge({
  availability,
  waitlist,
}: {
  availability?: string
  waitlist?: boolean
}) {
  if (!availability || availability === "unknown") return null
  const label = waitlist && availability === "full" ? "waitlist" : availability
  const key = waitlist && availability === "full" ? "waitlist" : availability
  return (
    <Badge className={cn("font-normal", AVAILABILITY_COLOR[key] ?? "bg-gray-100 text-gray-500")}>
      {label}
    </Badge>
  )
}

function ModeBadge({ mode }: { mode: string }) {
  return (
    <Badge className={cn("font-normal", MODE_COLOR[mode] ?? "bg-gray-100 text-gray-700")}>
      {MODE_LABEL[mode] ?? mode ?? "-"}
    </Badge>
  )
}

function TierBadge({ tier }: { tier: string }) {
  return (
    <Badge className={cn("font-normal", TIER_COLOR[tier] ?? "bg-gray-100 text-gray-500")}>
      {TIER_LABEL[tier] ?? tier}
    </Badge>
  )
}

function Bool({ value }: { value: boolean }) {
  return value ? (
    <Check className="mx-auto size-4 text-emerald-600" />
  ) : (
    <X className="mx-auto size-4 text-muted-foreground" />
  )
}

function Dash() {
  return <span className="text-muted-foreground">-</span>
}

function orDash(value: string | number | null | undefined): React.ReactNode {
  if (value === null || value === undefined || value === "") return <Dash />
  return value
}
